import { makeAutoObservable, reaction } from "mobx";
import _ from "lodash";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";

import { getApi, getApiError, isSessionExpired } from "../../../api";
import { tokenManager } from "../../../api/tokenManager";
import { CreateEntityStore } from "../../common/store/types";
import { phonogramService } from "../services/phonogramService";
import {
  PhonogramValuesInit,
  getPhonogramValues,
  PhonogramValuesType,
  PhonogramErrorsType,
  phonogramDisplayId,
} from "./PhonogramCommonStore";
import { Phonogram, SortOrder } from "../../../api/graphql";
import {
  CustomValidator,
  runCustomValidators,
  collectCustomWarnings,
} from "../../common/validation/types";
import * as customValidations from "./customValidations";
import { resolveCurrentLang } from "../../../common/utils";
import {
  getInterpreterValues,
  InterpreterValuesType,
} from "../../interpreter/store/InterpreterCommonStore";
import { actualStatusList } from "../../common/types";
import { addedStatusList } from "../../common/types";
import { formatBytes } from "../../../common/utils";
import { isNil } from "../../../common/utils";
import { GridColumn } from "../../common/types";
import { MusicalWorkLinkCreateStore } from "../../musicalWorkLink/store";
import { apiUploadFile } from "../../../api/apiClient";
import { uploadFailureReason } from "../../../common/utils";
import { GridChangeSet } from "../../common/types";
import { restoredStatus } from "../../common/types";
import { updatedStatus, deleteStatus } from "../../common/types";
import { isOriginalCrudStatus } from "../../common/types";
import { interpreterService } from "../../interpreter/services/interpreterService";
import { InitializeParams } from "../../../store/types";

type AsyncFunction = () => Promise<any>;

export class PhonogramCreateStore implements CreateEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;

  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  validationErrorsDialogOpen: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  errors: PhonogramErrorsType = {};
  service = phonogramService;
  cardState: Record<string, { open: boolean; errors: string[] }> = {
    null: { open: true, errors: [] },
    phonogramInfo: { open: true, errors: [] },
  };
  navigationLinks: { id: string; label: string; errors: string }[] = [];
  savedId: string | null = null;
  // Mientras guarda: la capa del formulario (SavingOverlay) lo bloquea.
  isSaving: boolean = false;
  savedEntity: Phonogram | null = null;
  cancelled = false;
  callbackParams: CallbackDataParams | null = null;
  entityValues = PhonogramValuesInit();
  trackNameError: string | undefined;
  performersError: string | undefined;
  performersService = interpreterService;
  performersSearchValue: string = "";
  performersOptions: InterpreterValuesType[] = [];
  albumNameError: string | undefined;
  publicationYearError: string | undefined;
  isrcError: string | undefined;
  originalProducerError: string | undefined;
  audioFileError: string | undefined;
  mainAudioError: string | undefined;
  linksError: string | undefined;
  linksVisibleColumns: GridColumn[];
  linksCreateStore: MusicalWorkLinkCreateStore;
  notesError: string | undefined;
  private uniqueCounter = 1;
  dummyCRUDRestoredObservable: number = 0;
  private customValidators: CustomValidator<PhonogramValuesType>[] = [];
  private _ownPendingChanges = false;
  private originalSnapshot: Record<string, any> | null = null;
  private stopReaction: (() => void) | null = null;
  // Callback system for "Add new" flow
  isWaitingForCallback = false;
  callbackReturnPath: string | null = null;
  // Modal mode flag - when true, reference views open as stacked modals
  isModalMode = false;
  // Handler for inline create dialog — when set, close() calls this instead of navigating
  closeHandler:
    | ((returnData?: {
        resultId?: string;
        cancelled?: boolean;
        entity?: any;
      }) => void)
    | null = null;
  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    this.setNavigationLinks();
    this.linksVisibleColumns = [
      {
        name: "__platform",
        title: this.langStore.MusicalWorkLinkPlatformLabel,
      },
      { name: "__type", title: this.langStore.MusicalWorkLinkTypeLabel },
      { name: "url", title: this.langStore.MusicalWorkLinkUrlLabel },
    ];
    this.linksCreateStore = new MusicalWorkLinkCreateStore(
      rootStore,
      "links_" + this.storeId
    );
    // Auto-registra los validators custom exportados por customValidations.ts
    // (stub editable del proyecto). Evita tener que editar este store generado.
    Object.values(customValidations).forEach((v) => {
      if (v && typeof (v as any).validate === "function") {
        this.registerCustomValidator(v as CustomValidator<PhonogramValuesType>);
      }
    });
    makeAutoObservable(this);
    this.setupChangeDetection();
    this.saveOriginalState();
  }

  destroy() {
    if (this.stopReaction) {
      this.stopReaction();
    }
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  registerCustomValidator(validator: CustomValidator<PhonogramValuesType>) {
    this.customValidators.push(validator);
  }

  // Acceso resiliente a cardState: un card referenciado por un form pero no
  // inicializado (p.ej. un card manual del proyecto) NO debe crashear la página.
  // Los getters son puros (no mutan durante el render); los mutadores auto-crean
  // la entrada faltante con un default abierto y sin errores.
  toggleCardState(id: string) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = !this.cardState[id].open;
  }

  setCardState(id: string, status: boolean) {
    if (!this.cardState[id]) this.cardState[id] = { open: true, errors: [] };
    this.cardState[id].open = status;
  }

  getCardState(id: string) {
    return this.cardState[id]?.open ?? true;
  }

  getCardErrors(id: string) {
    return this.cardState[id]?.errors?.join(", ") ?? "";
  }

  setCardError(id: string, message: string) {
    const state = this.cardState[id] ?? { open: true, errors: [] };
    this.cardState = {
      ...this.cardState,
      ...{
        [id]: { open: state.open, errors: [...state.errors, message] },
      },
    };
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get isProcessing() {
    return this.status === "processing";
  }

  get displayId(): string {
    if (this.entityValues) {
      return phonogramDisplayId(this.entityValues as any, this.langStore);
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.PhonogramSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  showValidationErrorsDialog() {
    this.validationErrorsDialogOpen = true;
  }

  closeValidationErrorsDialog() {
    this.validationErrorsDialogOpen = false;
  }

  setContentTop(_contentTop: number) {}

  setCRUDRowsFromValues(changes: GridChangeSet, values: { string: any }) {
    if (changes.added) {
      changes.added = [values];
    } else if (changes.changed) {
      changes.changed = [values];
    }
  }

  *returnedData(params: CallbackDataParams): any {
    if (
      params.returnData &&
      !params.returnData.cancelled &&
      (params.returnData.entity || params.returnData.resultId)
    ) {
      // Delegate to subentity CreateStore if callerData.entityName matches a subentity
      if (params.callerData?.entityName === "MusicalWorkLink") {
        this.linksCreateStore.isWaitingForCallback = false;
        this.linksCreateStore.callbackReturnPath = null;
        yield this.linksCreateStore.returnedData(params);
        return;
      }

      if (params.callerData?.fieldName === "performers") {
        if (params.returnData.entity) {
          const displayId =
            params.returnData.entity.displayId ||
            String(params.returnData.entity.id);
          this.entityValues.showPerformers.push({
            displayId,
            id: params.returnData.entity.id,
          });
        } else if (params.returnData.resultId) {
          const api = yield* getApi(
            this.rootStore.authStore,
            this.rootStore.activityStore
          );
          const result = yield api.interpreterOne({
            id: params.returnData.resultId,
          });
          const interpreter = result.interpreterOne;
          this.entityValues.showPerformers.push({
            displayId: interpreter.displayId,
            id: interpreter.id,
          });
          this.setPerformersOptions([
            getInterpreterValues(interpreter, this.langStore),
          ]);
        }
      }
    }
  }

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  setNavigationLinks() {
    this.navigationLinks = [
      {
        id: "phonogramInfo",
        label: this.rootStore.langStore.groupPhonogramPhonogramInfoLabel,
        errors: this.getCardErrors("phonogramInfo"),
      },
    ];
  }

  getTrackNameValue(): string {
    return this.entityValues.trackName;
  }

  setTrackNameValue(value: string) {
    this.entityValues.trackName = value;
  }

  setPerformersOptions(values: InterpreterValuesType[]) {
    this.performersOptions = values;
  }

  *loadPerformersValues(): any {
    let where: any = null;
    if (this.performersSearchValue) {
      const searchFilter = {
        name: { contains: this.performersSearchValue, mode: "insensitive" },
      };
      where = where ? { AND: [where, searchFilter] } : searchFilter;
    }
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.interpreterListWithOffset({
      offset: 0,
      take: 100,
      where: where,
      orderBy: [{ lastUpdatedAt_: SortOrder.Desc }],
    });
    this.setPerformersOptions(
      (result.interpreterListWithOffset || []).map((x: any) =>
        getInterpreterValues(x, this.langStore)
      )
    );
  }

  getPerformersOptions() {
    return this.performersOptions.map((x: any) => {
      return {
        displayId: resolveCurrentLang(x.displayId, this.langStore),
        id: x.id,
      };
    });
  }

  *setPerformersValue(value: string) {
    this.performersSearchValue = value;
    yield this.loadPerformersValues();
  }

  setSelectedPerformers(values: { displayId: string; id: string }[]) {
    if (values.length > 0 && values[values.length - 1].id === "add-new") {
      const notFound = /".*?"/.exec(values[values.length - 1].displayId);
      this.addNewInterpreter(
        notFound ? notFound[0].replaceAll('"', "") : undefined,
        "performers"
      );
    } else {
      this.entityValues.showPerformers = values;
      this.entityValues.__performers = values.map((v) => v.displayId);
    }
  }

  getAlbumNameValue(): string {
    return this.entityValues.albumName;
  }

  setAlbumNameValue(value: string) {
    this.entityValues.albumName = value;
  }

  getPublicationYearValue(): number | undefined {
    return this.entityValues.publicationYear;
  }

  setPublicationYearValue(value: number | undefined) {
    this.entityValues.publicationYear = value;
  }

  getIsrcValue(): string {
    return this.entityValues.isrc;
  }

  setIsrcValue(value: string) {
    this.entityValues.isrc = value;
  }

  getOriginalProducerValue(): string {
    return this.entityValues.originalProducer;
  }

  setOriginalProducerValue(value: string) {
    this.entityValues.originalProducer = value;
  }

  audioFileRestoreFile(id: string) {
    if (this.entityValues.audioFile.length > 1) {
      alert("No puede haber más de un archivo activo " + id);
      return;
    }
  }

  *audioFileUploadFile(file: File): any {
    const previousFiles = [...this.entityValues.__audioFile_files];
    const previousName = this.entityValues.audioFile;
    const fileId = `${this.uniqueCounter++}`;
    const newFile = {
      __status__: "added" as const,
      id: fileId,
      name: file.name,
      size: file.size,
      formattedSize: formatBytes(file.size),
      mimetype: file.type,
      ticket: "",
    };
    this.entityValues.__audioFile_files.push(newFile);
    this.entityValues.audioFile = file.name;
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;

    const outcome = yield this.uploadTicket(file);
    if (!outcome.ticket) {
      // La subida no terminó: el servidor no tiene el archivo, así que no
      // puede quedar en la lista. Vuelve lo que había y el campo dice por qué.
      this.entityValues.__audioFile_files = previousFiles;
      this.entityValues.audioFile = previousName;
      this.dummyCRUDRestoredObservable = this.uniqueCounter++;
      if (outcome.cancelled) {
        // Lo canceló el usuario desde el modal de espera: no es una falla.
        this.clearFieldError("audioFile");
      } else {
        this.setFieldError(
          "audioFile",
          this.langStore.PhonogramAudioFileLabel,
          this.langStore.uploadFailedMessage(file.name, outcome.reason)
        );
      }
      return;
    }
    this.clearFieldError("audioFile");
    const fileIndex = this.entityValues.__audioFile_files.findIndex(
      (f) => f.id === fileId
    );
    if (fileIndex !== -1) {
      this.entityValues.__audioFile_files[fileIndex].ticket = outcome.ticket;
    }
  }

  audioFileCommitFile(changes: GridChangeSet) {
    if (changes.deleted) {
      const id = changes.deleted[0];
      let index = 0;
      let toRemove;
      for (let file of this.entityValues.__audioFile_files) {
        if (file.id === id) {
          toRemove = index;
          break;
        }
        index++;
      }
      if (toRemove) {
        this.entityValues.__audioFile_files.splice(toRemove, 1);
      }
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  getMainAudioValue(): boolean {
    return this.entityValues.mainAudio;
  }

  setMainAudioValue(value: boolean) {
    this.entityValues.mainAudio = value;
  }

  linksLoadRow(row: any) {
    this.linksCreateStore.loadValuesFromRow(row);
  }

  linksRestore(id: string) {
    const links = this.entityValues.showLinks.filter((x) => x.id === id)[0];
    if (links) {
      links.__status__ = restoredStatus(links.__status__);
    }
    this.dummyCRUDRestoredObservable = this.uniqueCounter++;
  }

  linksValidateChanges(_changes: GridChangeSet) {
    const errors = this.linksCreateStore.validateData(
      this.linksCreateStore.entityValues
    );

    this.linksCreateStore.errors = errors;
    return Object.keys(errors).length === 0;
  }

  linksCommitChanges(changes: GridChangeSet) {
    if (changes.changed) {
      const values = this.linksCreateStore.entityValues;
      const links = this.entityValues.showLinks;
      for (let index = 0; index < this.entityValues.showLinks.length; index++) {
        if (links[index].id === values.id) {
          links[index] = {
            ...values,
            __status__: updatedStatus(values.__status__),
          };
          break;
        }
      }
      this.linksCreateStore.clearFields();
    } else if (changes.added) {
      const values = this.linksCreateStore.entityValues;
      if (!this.entityValues.showLinks) {
        this.entityValues.showLinks = [];
      }
      this.entityValues.showLinks = [
        ...this.entityValues.showLinks,
        {
          ...values,
          id: `${this.uniqueCounter++}`,
          __status__: "added",
        },
      ];
      this.linksCreateStore.clearFields();
    } else if (changes.deleted) {
      const id = changes.deleted[0];
      const links = this.entityValues.showLinks;
      for (let index = 0; index < this.entityValues.showLinks.length; index++) {
        if (links[index].id === id) {
          if (isOriginalCrudStatus(links[index].__status__)) {
            links[index] = {
              ...links[index],
              __status__: deleteStatus(links[index].__status__),
            };
          } else {
            this.entityValues.showLinks = this.entityValues.showLinks.filter(
              (x) => x.id !== id
            );
          }
          break;
        }
      }
    }

    // if (changes.changed) {
    // } else {
    //   const newChanges = getFixedChangeSet(changes, this.entityValues.links);
    //   this.setCRUDRowsFromValues(newChanges, this.linksCreateStore.entityValues);
    //   this.dummyCRUDRestoredObservable = this.uniqueCounter++;
    //   this.entityValues.links = getNewCRUDRows(newChanges, this.entityValues.links, this.uniqueCounter++);
    // }
  }

  getNotesValue(): string {
    return this.entityValues.notes;
  }

  setNotesValue(value: string) {
    this.entityValues.notes = value;
  }

  *fileUpload(file: File): any {
    const data = yield apiUploadFile(true, file, this.rootStore.activityStore);
    // Una subida cancelada vuelve sin datos.
    return data?.singleUpload?.ticket;
  }

  *uploadTicket(file: File): any {
    // No tira: una subida que falla es un resultado que el campo tiene que
    // mostrar, no una excepción que se pierde en la consola.
    try {
      const ticket = yield this.fileUpload(file);
      // Sin ticket y sin error es un pedido abortado: el botón Cancelar.
      return ticket ? { ticket } : { cancelled: true };
    } catch (error) {
      return {
        reason: uploadFailureReason(error, this.langStore.uploadConnectionLost),
      };
    }
  }

  setFieldError(field: string, fieldLabel: string, message: string) {
    this.errors = {
      ...this.errors,
      [field]: { message, fieldLabel },
    } as PhonogramErrorsType;
  }

  clearFieldError(field: string) {
    if (!(field in this.errors)) return;
    const errors: Record<string, unknown> = { ...this.errors };
    delete errors[field];
    this.errors = errors as PhonogramErrorsType;
  }

  *fileDelete(ticket: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    yield api.deleteUploadedFile({ ticket });
  }

  addNewInterpreter(notFound: string | undefined, callerFieldName?: string) {
    this.isWaitingForCallback = true;
    this.callbackReturnPath = window.location.pathname;
    // Protect parent store in registry from destruction during navigation
    const parentStore = this.rootStore.storeRegistry.getStoreIfExists(
      this.callbackReturnPath
    );
    if (parentStore) {
      parentStore.isWaitingForCallback = true;
    }
    const callbackParams: CallbackDataParams = {
      storeId: this.callbackReturnPath,
      callerData: {
        entityName: "Phonogram",
        fieldName: callerFieldName || "",
        isMulti: true,
        notFound: notFound,
      },
    };

    this.rootStore.router.goTo(
      "/interpreter/create",
      undefined,
      undefined,
      { callbackParams, initialDisplayId: notFound },
      this.breadcrumbLabel
    );
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.entityValues = PhonogramValuesInit();
    this.cardState = {
      null: { open: true, errors: [] },
      phonogramInfo: { open: true, errors: [] },
    };
    this.performersSearchValue = "";
    this.performersOptions = [];
  }

  clearErrors() {
    Object.keys(this.cardState).forEach((key) => {
      this.cardState[key].errors = [];
    });
  }

  validateData(values: PhonogramValuesType): PhonogramErrorsType {
    const errors: PhonogramErrorsType = {};
    this.clearErrors();
    values.trackName = values.trackName.trim();
    if (values.trackName === "") {
      errors.trackName = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PhonogramTrackNameLabel,
      };
      this.setCardError("phonogramInfo", errors.trackName.message);
    }

    if (
      values.__audioFile_files.filter((f: any) =>
        actualStatusList.includes(f.__status__)
      ).length === 0
    ) {
      errors.audioFile = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PhonogramAudioFileLabel,
      };
      this.setCardError("phonogramInfo", errors.audioFile.message);
    }
    {
      const pendingUpload = values.__audioFile_files.find(
        (f: any) => addedStatusList.includes(f.__status__) && !f.ticket
      );
      if (pendingUpload) {
        errors.audioFile = {
          message: this.langStore.uploadNotFinishedMessage(pendingUpload.name),
          fieldLabel: this.langStore.PhonogramAudioFileLabel,
        };
        this.setCardError("phonogramInfo", errors.audioFile.message);
      }
    }

    if (isNil(values.mainAudio)) {
      errors.mainAudio = {
        message: this.langStore.mandatoryField,
        fieldLabel: this.langStore.PhonogramMainAudioLabel,
      };
      this.setCardError("phonogramInfo", errors.mainAudio.message);
    }

    // Custom validators (registrados desde customValidations.ts). Se corren
    // siempre, aunque la entidad no tenga validaciones de campo generadas.
    const customValidationResult = runCustomValidators(
      this.customValidators,
      values,
      this.langStore,
      this.rootStore.referenceCacheStore
    );
    Object.assign(errors, customValidationResult.errors);
    if (customValidationResult.cardErrors) {
      for (const [cardId, messages] of Object.entries(
        customValidationResult.cardErrors
      )) {
        messages.forEach((msg) => this.setCardError(cardId, msg));
      }
    }

    this.setNavigationLinks();
    return errors;
  }

  // Avisos de los validators custom: ver collectCustomWarnings en validation/types.ts.
  get warnings(): Record<string, { message: string; fieldLabel: string }> {
    return collectCustomWarnings(
      this.customValidators,
      { ...this.entityValues },
      this.langStore,
      this.rootStore.referenceCacheStore,
      undefined,
      "PhonogramCreateStore"
    );
  }

  *duplicatesPhonogram(id: string): any {
    const validToken = yield tokenManager.getValidAccessToken();
    if (!validToken) {
      throw new Error("SESSION_EXPIRED");
    }
    this.rootStore.authStore.accessToken = validToken;
    const phonogramInstance = yield this.service.getOne(
      id,
      validToken,
      this.rootStore.activityStore
    );
    if (phonogramInstance) {
      this.entityValues = getPhonogramValues(
        phonogramInstance,
        this.langStore,
        true
      );
    }
  }

  close(returnData?: { resultId?: string; cancelled?: boolean; entity?: any }) {
    if (this.closeHandler) {
      this.closeHandler(returnData);
      return;
    }

    if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
      return;
    }

    if (this.callbackReturnPath) {
      // Sync breadcrumbs to return path (truncates stack instead of clearing)
      this.rootStore.syncBreadcrumbsWithBrowser(this.callbackReturnPath);

      // Navigate back with callback data (include original callerData)
      // Use JSON parse/stringify to ensure the state is structurally clonable for History API
      const callbackParams: CallbackDataParams = JSON.parse(
        JSON.stringify({
          storeId: this.callbackReturnPath,
          callerData: this.callbackParams?.callerData,
          returnData: returnData || { cancelled: true },
        })
      );
      this.rootStore.router.replace(this.callbackReturnPath, {
        callbackParams,
      });
    } else if (returnData?.resultId && !this.rootStore.router.canGoBack()) {
      // Sin página anterior (una pestaña nueva), volver atrás no navega: el
      // formulario quedaría abierto, y un segundo Guardar daría de alta otro
      // registro. Pasa a la vista del registro creado.
      this.rootStore.router.replace(`/phonogram/view/${returnData.resultId}`);
    } else {
      this.rootStore.router.goBack();
    }
  }

  *saveNewPhonogram(): any {
    // Un segundo Guardar mientras corre el primero no hace nada: daría de alta
    // el registro dos veces.
    if (this.isProcessing) return;
    this.errors = { ...this.validateData(this.entityValues) };
    if (Object.keys(this.errors).length === 0) {
      this.closeValidationErrorsDialog();

      // El id de ESTE guardado. Si falla antes de llegar al server (al armar el
      // pedido), no queda el de uno anterior que cierre como si éste hubiera
      // salido bien.
      this.savedId = null;
      // Mientras manda, el formulario está bloqueado por su capa.
      this.isSaving = true;
      try {
        this.status = "processing";
        yield this._save();
        this.status = "ok";
      } catch (error) {
        this.status = "error";

        if (error instanceof Error) {
          const apiError = getApiError(error, this.langStore);
          this.showErrorDialog(apiError.code, apiError.title, apiError.message);
        } else {
          this.showErrorDialog(this.langStore.serverError, undefined);
        }
      } finally {
        this.isSaving = false;
      }

      if (this.savedId) {
        this.saveOriginalState();
        this.close({
          resultId: this.savedId,
          entity: { id: this.savedId, displayId: this.displayId },
          cancelled: false,
        });
      }
    } else {
      this.showValidationErrorsDialog();
    }
  }

  cancelNewPhonogram(): void {
    this.cancelled = true;

    // Navigation handled by component

    this.close({ cancelled: true });
  }

  get pendingChanges(): boolean {
    return this._ownPendingChanges;
  }

  hasUnsavedChanges(): boolean {
    return this.pendingChanges;
  }

  // Public interface for navigation blocking system
  get hasPendingChanges(): boolean {
    return this.pendingChanges;
  }

  /**
   * Computes a normalized snapshot for change detection.
   * - Simple fields: direct value
   * - Single refs: normalized to ID only
   * - Multi refs: normalized to sorted array of IDs
   */
  private computeSnapshot(): Record<string, any> {
    return {
      trackName: this.entityValues.trackName,
      albumName: this.entityValues.albumName,
      publicationYear: this.entityValues.publicationYear,
      isrc: this.entityValues.isrc,
      originalProducer: this.entityValues.originalProducer,
      mainAudio: this.entityValues.mainAudio,
      notes: this.entityValues.notes,
      performers: (this.entityValues.showPerformers || [])
        .map((x: any) => x.id)
        .sort(),
    };
  }

  private setupChangeDetection() {
    // Use normalized snapshot with deep equality comparison
    this.stopReaction = reaction(
      () => this.computeSnapshot(),
      (snapshot) => {
        const hasChanges = this.originalSnapshot
          ? !_.isEqual(snapshot, this.originalSnapshot)
          : false;

        if (this._ownPendingChanges !== hasChanges) {
          this._ownPendingChanges = hasChanges;
        }
      },
      { fireImmediately: false, equals: (a, b) => _.isEqual(a, b) }
    );
  }

  private saveOriginalState() {
    this.originalSnapshot = this.computeSnapshot();
    this._ownPendingChanges = false;
  }

  getState(): InitializeParams {
    return {
      uiState: {
        cardState: this.cardState,
      },
    };
  }

  loadValuesFromRow(row: any) {
    const values = getPhonogramValues(row, this.langStore);
    // Filas nuevas de la grilla traen SOLO las columnas visibles: los campos
    // ausentes (undefined) caen al init — sin esto, validaciones tipo
    // values.x.trim() explotan y los inputs quedan uncontrolled.
    const init = PhonogramValuesInit();
    for (const k of Object.keys(init) as (keyof typeof init)[]) {
      if ((values as any)[k] === undefined)
        (values as any)[k] = (init as any)[k];
    }
    this.entityValues = values;
  }

  *_save(): any {
    // Subentidad sin alta standalone: se persiste por el create/update anidado del parent.
  }

  *process(fn: AsyncFunction) {
    try {
      this.status = "processing";
      yield fn();
      this.status = "ok";
    } catch (error) {
      // El estado primero: si el diálogo fallara, Guardar no queda trabado.
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        this.showErrorDialog(apiError.code, apiError.title, apiError.message);
      } else {
        this.showErrorDialog(this.langStore.serverError, undefined);
      }
    }
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const duplicateFromId = params?.duplicateFromId;

      // Si se proporciona un ID para duplicar (desde router.params)
      if (duplicateFromId) {
        yield this.duplicatesPhonogram(duplicateFromId);
      }

      // Si se proporciona initialDisplayId (desde "Añadir 'texto'" en autocomplete)
      const initialDisplayId = params?.initialDisplayId;
      if (initialDisplayId) {
        this.entityValues.trackName = initialDisplayId;
      }

      yield this.loadPerformersValues();

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
